import React, {
    useState,
    useEffect,
    useMemo,
    useContext,
    useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import connect from '../../../utils/connect';
import combineActions from '../../../utils/combineActions';

import sportsApi from '../../../utils/sportsApi';

import SearchSelect from '@app/ui/sports-components/SearchSelect/index.js';
import SinglePlacement from '../SinglePlacement';
import PlacementName from '../PlacementName';

import { Row, Col, FormGroup, FormGroupLabel } from '@app/ui';
import Tooltip from '@app/ui/sports-components/Tooltip';

import removePlacement from '../../../actions/settings/removePlacement';
import sendNotification from '../../../actions/notifications/send';

import RPCard from '../../RPCard';
import { Context } from '../../contexts/OnboardingContext';
import { PlacementContext } from '../../contexts/PlacementContext';
import { getPlacementActive } from '../../../utils/getActiveOnboardingInstances';
import { STANDARD_PLACEMENT_EXPERIENCE } from '../../../constants/config';

const ConfirmedPlacements = ({
    client,
    removePlacement: removeOnboardingPlacement,
    sendNotification,
}) => {
    const { t } = useTranslation('settings');
    const {
        rhId,
        structureLocked,
        setIsRemovingSP,
        currentSelectedPlacement,
        setCurrentSelectedPlacement,
        setSponsorsToAdd,
        sponsorsToRemove,
        setSponsorsToRemove,
        placements,
        maxPlacements,
        hasStandardPlacements,
    } = useContext(Context);

    const {
        showPlacementUploader,
        setShowPlacementUploader,
        placementTypeOptions,
        placementOptions,
        placementTypesLoading,
        isNew,
        setIsNew,
        placementTypeId,
        setPlacementTypeId,
        setPrevFiles,
        rhPls,
        setAlias,
        alias,
        addToSponsorList,
    } = useContext(PlacementContext);

    const [placementOption, setPlacementOption] = useState(null);
    const hasMaxPlacements =
        !!maxPlacements &&
        !client.permissions.includes(STANDARD_PLACEMENT_EXPERIENCE);

    useEffect(() => {
        if (!showPlacementUploader) {
            setAlias('');
        }
    }, [showPlacementUploader]);

    useEffect(() => {
        if (placementOption || Number.isInteger(placementOption)) {
            setPlacementOption(null);
        }
    }, [rhId]);

    useEffect(() => {
        const last = sponsorsToRemove[sponsorsToRemove.length - 1];
        addToSponsorList(last);
    }, [sponsorsToRemove]);

    const currentPlacementId = currentSelectedPlacement
        ? currentSelectedPlacement.id
        : null;

    useEffect(() => {
        setSponsorsToAdd([]);
        setSponsorsToRemove([]);
    }, [currentPlacementId]);

    const placementType = placementTypeOptions.find(
        p => p.value === placementTypeId,
    );

    const newPlacementsOptions = placementTypeOptions.reduce((acc, po) => {
        if (
            placements.findIndex(
                p =>
                    p.placementType &&
                    (p.placementType.id === po.value || p.name === po.name),
            ) > -1
        )
            return acc;
        if (acc.findIndex(p => p.value === po.value) > -1) return acc;
        acc.push(po);
        return acc;
    }, []);

    const searchPlacementOptions = placementOptions.filter(po =>
        placements.find(p => p.name.toUpperCase() === po.name.toUpperCase()),
    );

    const selectPlacement = useCallback(
        pl => () => {
            const isPlacementActive = getPlacementActive(pl);
            if (!isPlacementActive) return;
            setCurrentSelectedPlacement(pl);
            if (pl.placementType && pl.placementType.id)
                setPlacementTypeId(pl.placementType.id);
            setShowPlacementUploader(true);
            setIsNew(false);
            if (pl.examples) {
                const newPrevFiles = pl.examples.map(e => {
                    const p = { ...e };
                    delete p.isNew;
                    return p;
                });
                setPrevFiles(newPrevFiles);
            }
        },
        [
            setCurrentSelectedPlacement,
            setPlacementTypeId,
            setShowPlacementUploader,
            setIsNew,
            setPrevFiles,
        ],
    );

    const deletePlacement = useCallback(
        pl => async e => {
            e.stopPropagation();
            try {
                if (structureLocked) return;
                setIsRemovingSP(true);
                const confirmMessage = t(
                    'clientConfiguration.onboarding.deletePlacementConfirm',
                    { name: pl.name },
                );
                const confirmation = confirm(confirmMessage);
                if (!confirmation) return;
                const endPoint = `/clients/${client.id}/rightsholders/${rhId}/placements/${pl.id}`;

                const deletedPlacement = await sportsApi
                    .url(endPoint)
                    .delete()
                    .json();

                const shouldDeletePermenantly = !!(
                    !deletedPlacement ||
                    Object.keys(deletedPlacement).length === 0
                );

                await removeOnboardingPlacement(
                    { rhId, placement: pl },
                    shouldDeletePermenantly,
                );

                setIsRemovingSP(false);
            } catch (e) {
                // eslint-disable-next-line no-console
                console.log(e);
                setIsRemovingSP(false);
                const message = t(
                    'clientConfiguration.onboarding.deactivateError',
                );
                sendNotification(message, {
                    context: 'danger',
                    description: t(
                        'clientConfiguration.onboarding.deactivateErrorContact',
                    ),
                });
            }
        },
        [structureLocked, rhId, client, t],
    );

    const updateAlias = e => {
        const value = e.target.value;
        setAlias(value);
    };

    const showPlacementInfo = !!currentSelectedPlacement;

    const placementList = useMemo(() => {
        const plts =
            (showPlacementUploader && isNew) ||
            !Number.isInteger(placementOption) ||
            !placementOptions ||
            placementOptions.length === 0 ||
            !placementOptions[placementOption]
                ? placements
                : placements.filter(p => {
                      const po = placementOptions.find(
                          o => o.value === placementOption,
                      );
                      return po.name === p.name;
                  });

        const activePlts = plts
            .filter(p => getPlacementActive(p))
            .sort(
                (a, b) =>
                    typeof a.name === 'string' && a.name.localeCompare(b.name),
            );
        const inactivePlts = plts
            .filter(p => !getPlacementActive(p))
            .sort(
                (a, b) =>
                    typeof a.name === 'string' && a.name.localeCompare(b.name),
            );

        return [...activePlts, ...inactivePlts];
    }, [
        placementOption,
        placementOptions,
        showPlacementUploader,
        isNew,
        placements,
        rhId,
    ]);

    useEffect(() => {
        if (Array.isArray(rhPls)) {
            const alreadyExists =
                rhPls.findIndex(
                    pl =>
                        pl.placementType &&
                        pl.placementType.id === placementTypeId,
                ) > -1;
            let name = '';
            if (!alreadyExists) {
                name = placementType ? placementType.name : '';
            } else {
                const rhPl = rhPls.find(
                    pl =>
                        pl.placementType &&
                        pl.placementType.id === placementTypeId,
                );
                name = rhPl.name;
            }
            setAlias(name);
        }
    }, [placementTypeId, rhPls]);

    const getDisplayName = () => {
        if (!currentSelectedPlacement) return;
        if (hasStandardPlacements) {
            return currentSelectedPlacement.name || '';
        }
        if (
            currentSelectedPlacement.placementType &&
            currentSelectedPlacement.placementType.name !==
                currentSelectedPlacement.name
        ) {
            return `${currentSelectedPlacement.placementType.name} (${currentSelectedPlacement.name})`;
        }
        return currentSelectedPlacement.name || '';
    };

    const displayName = getDisplayName();

    const disableAlias =
        !placementTypeId ||
        (Array.isArray(rhPls) &&
            rhPls.findIndex(
                pl =>
                    pl.placementType && pl.placementType.id === placementTypeId,
            ) > -1);
    const placementsCount = placements.filter(getPlacementActive).length;

    return (
        <>
            {!showPlacementInfo && (
                <div className="gds-flex gds-flex--align-center">
                    <div className="gds-flex__item gds-flex__item--grow-0 -m-r-3">
                        <label
                            className="gds-form-group__label -m-b-0"
                            htmlFor="input-2"
                        >
                            {hasMaxPlacements
                                ? t(
                                      'clientConfiguration.onboarding.placementsCountWithMax',
                                      {
                                          count: placementsCount || 0,
                                          max: maxPlacements,
                                      },
                                  )
                                : t(
                                      'clientConfiguration.onboarding.placementsCount',
                                      {
                                          count: placementsCount || 0,
                                      },
                                  )}
                        </label>
                    </div>
                    {hasMaxPlacements && (
                        <div className="gds-flex__item gds-flex__item--grow-1">
                            <div
                                className="gds-progress-bar gds-progress-bar--secondary gds-progress-bar--xs -m-v-2"
                                data-value={`${Math.floor(
                                    ((placementsCount || 0) / maxPlacements) *
                                        100,
                                )}`}
                            />
                        </div>
                    )}
                </div>
            )}

            <div className="gds-form-group -m-t-2">
                {!showPlacementUploader && (
                    <SearchSelect
                        name="placement-select"
                        size="sm"
                        placeholder={
                            showPlacementUploader
                                ? t(
                                      'clientConfiguration.onboarding.selectPlacement',
                                  )
                                : t(
                                      'clientConfiguration.onboarding.searchPlacement',
                                  )
                        }
                        options={searchPlacementOptions}
                        onChange={setPlacementOption}
                        value={placementOption}
                        disable={placementTypesLoading}
                    />
                )}
                {showPlacementUploader && isNew && (
                    <Row gutter={16}>
                        <Col md={12}>
                            <div className="gds-flex__item gds-flex__item--grow-1 gds-flex__item--shrink-0 post-filter-input">
                                <FormGroup layout="vertical">
                                    <FormGroupLabel
                                        text={t(
                                            'clientConfiguration.onboarding.placements',
                                        )}
                                    />
                                    <SearchSelect
                                        name="placement-select"
                                        size="sm"
                                        placeholder={
                                            showPlacementUploader
                                                ? t(
                                                      'clientConfiguration.onboarding.selectPlacement',
                                                  )
                                                : t(
                                                      'clientConfiguration.onboarding.searchPlacement',
                                                  )
                                        }
                                        options={newPlacementsOptions}
                                        onChange={setPlacementTypeId}
                                        value={placementTypeId}
                                        disable={placementTypesLoading}
                                    />
                                </FormGroup>
                            </div>
                        </Col>
                        <Col md={12}>
                            <div className="gds-flex__item gds-flex__item--grow-1 gds-flex__item--shrink-0 post-filter-input -m-r-0-sm">
                                <FormGroup layout="vertical">
                                    <FormGroupLabel
                                        text={t(
                                            'clientConfiguration.onboarding.displayNameOptional',
                                        )}
                                    />
                                    <Tooltip
                                        text={t(
                                            'clientConfiguration.onboarding.displayNameTooltip',
                                        )}
                                        position="top-right"
                                        className="-p-l-1"
                                    >
                                        <i className="fas fa-info-circle -cursor--pointer gds-text--body-sm" />
                                    </Tooltip>
                                    <input
                                        className={`gds-form-group__text-input gds-form-group__text-input--sm ${
                                            disableAlias
                                                ? `--disable_display_name`
                                                : ''
                                        }`}
                                        type="text"
                                        placeholder={t(
                                            'clientConfiguration.onboarding.enterDisplayName',
                                        )}
                                        data-gds-search-input=""
                                        id="enterAlias"
                                        value={alias}
                                        onChange={updateAlias}
                                        disabled={disableAlias}
                                    />
                                </FormGroup>
                            </div>
                        </Col>
                    </Row>
                )}
                {currentSelectedPlacement &&
                    showPlacementUploader &&
                    !isNew && (
                        <PlacementName selectedPlacementName={displayName} />
                    )}
            </div>

            {(showPlacementInfo || showPlacementUploader) && (
                <SinglePlacement />
            )}

            {!showPlacementInfo && !showPlacementUploader && (
                <div className="gds-flex-grid__container gds-placements-container -overflow-y-scroll -p-a-2 -m-t-3">
                    {placementList.length === 0 ? (
                        <div
                            className="gds-flex gds-flex--justify-center gds-flex--align-center"
                            style={{ minHeight: '300px', width: '100%' }}
                        >
                            <p>
                                {t(
                                    'clientConfiguration.onboarding.noPlacementsYet',
                                )}
                            </p>
                        </div>
                    ) : (
                        <Row gutter={16}>
                            {placementList.map((pl, index) => {
                                return (
                                    <Col
                                        md={12}
                                        className="-m-b-2"
                                        key={`placement_${index}`}
                                    >
                                        <RPCard
                                            placement={pl}
                                            selectPlacement={selectPlacement(
                                                pl,
                                            )}
                                            deletePlacement={deletePlacement(
                                                pl,
                                            )}
                                            structureLocked={structureLocked}
                                        />
                                    </Col>
                                );
                            })}
                        </Row>
                    )}
                </div>
            )}
        </>
    );
};

ConfirmedPlacements.propTypes = {
    client: PropTypes.object,
    removePlacement: PropTypes.func,
    sendNotification: PropTypes.func,
};

export default connect(
    ['user', 'client'],
    combineActions({
        removePlacement,
        sendNotification,
    }),
)(ConfirmedPlacements);
